import { alpha, Palette } from '@mui/material';

import { AircraftEventLegacy } from '#entities';
import {
  EAircraftEventStatus,
  EIGHT_HOURS,
  FOUR_HOURS,
  TWO_HOURS,
} from '#shared';

export type OrganColors = Record<string, string>; // e.g., ORGAN_COLORS

export const NEUTRAL = '#7F8083';

export const getEventTime = (eventTimeMs: number): number => {
  const eventTime = Math.floor(eventTimeMs / 1000 / 60);

  return Math.min(EIGHT_HOURS, Math.max(1, eventTime));
};

export const getCurrentEventLegacy = (
  events: AircraftEventLegacy[],
): AircraftEventLegacy => {
  const currentEvent = events[0] || {
    Type: EAircraftEventStatus.NotAvailable,
  };
  const nextEvent = events[1] || {
    Type: EAircraftEventStatus.NotAvailable,
  };
  const isCurrentOffDuty = currentEvent.Type === EAircraftEventStatus.OffDuty;
  const isCurrentLessTwoHours =
    Math.floor(currentEvent.EventTimeMs / 1000 / 60) < TWO_HOURS;
  const isNextOnDuty = nextEvent.Type === EAircraftEventStatus.OnDuty;

  if (isCurrentOffDuty && isCurrentLessTwoHours && isNextOnDuty) {
    return nextEvent;
  }

  return currentEvent;
};

export const getTimeOnDutyLabel = (timeOnDutyMs: number): string => {
  if (timeOnDutyMs >= EIGHT_HOURS) {
    return '8+';
  }
  if (timeOnDutyMs >= FOUR_HOURS && timeOnDutyMs < EIGHT_HOURS) {
    return '4-8';
  }
  if (timeOnDutyMs > 0 && timeOnDutyMs < FOUR_HOURS) {
    return '<4';
  }
  return 'X';
};

/** Previous behavior preserved: >=8h -> white, 4–8 -> yellow, 0–4 -> gray */
export const dutyColorFromMs = (ms: number): string => {
  if (ms >= EIGHT_HOURS) {
    return '#FFFFFF';
  } // white
  if (ms >= FOUR_HOURS) {
    return '#FFDA38';
  } // yellow
  return NEUTRAL; // gray
};

export const withBg = (fg: string, bgAlpha = 0.15) => ({
  fg,
  bg: alpha(fg, bgAlpha),
});

export const withBorder = (
  fg: string,
  insideWidget: boolean,
  borderAlpha = 0.5,
) => ({
  border: insideWidget ? alpha(fg, borderAlpha) : fg,
  hover: fg,
});

/** Color resolver for an event row/badge */
export const getEventColors = (opts: {
  eventType: EAircraftEventStatus;
  isCurrentEvent?: boolean;
  eventElapsedMs?: number; // only meaningful for OnDuty + current event
  organName?: string | null; // used for OnTrip
  isReturnToBase?: boolean;
  organColors: OrganColors; // ORGAN_COLORS
  palette?: Palette; // reserved for future palette-based theming
}): { fg: string; bg: string } => {
  const {
    eventType,
    isCurrentEvent = false,
    eventElapsedMs = 0,
    organName,
    isReturnToBase,
    organColors,
  } = opts;

  switch (eventType) {
    case EAircraftEventStatus.OnTrip: {
      const fg =
        organName && !isReturnToBase
          ? (organColors[organName] ?? NEUTRAL)
          : NEUTRAL;
      return withBg(fg);
    }

    case EAircraftEventStatus.OnDuty: {
      const fg = isCurrentEvent ? dutyColorFromMs(eventElapsedMs) : NEUTRAL;
      return withBg(fg);
    }

    case EAircraftEventStatus.OffDuty:
    case EAircraftEventStatus.NotAvailable:
    case EAircraftEventStatus.OnMaintenance: {
      return withBg(NEUTRAL);
    }

    default:
      return withBg(NEUTRAL);
  }
};

/** Card accent (text/border/hover) for main state chips/titles */
export const getCardAccents = (opts: {
  baseColor: string; // typically from organ / duty / neutral
  insideWidget: boolean;
}) => {
  const { baseColor, insideWidget } = opts;
  const { border, hover } = withBorder(baseColor, insideWidget);
  return {
    text: baseColor,
    border,
    hover,
  };
};

export const getMaintenanceAccent = (
  maintenanceType: { OpsBoardBadge?: string } | null | undefined,
  palette: Palette,
) => {
  if (!maintenanceType) {
    return null;
  }
  const isAog = maintenanceType.OpsBoardBadge === 'AOG';
  return isAog
    ? palette.aircraftStatus.aog
    : palette.aircraftStatus.maintenance;
};
